"use client";

import { useState, useEffect } from "react";
import MarkdownRenderer from "@/app/components/chat/MarkdownRenderer";
import { getContent, subscribeToContent } from "@/app/lib/content";
import type { HelpBlock, HelpDocument, TableBlock } from "@/app/lib/types";

// =============================================================================
// Types
// =============================================================================

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile?: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const TAB_STORAGE_KEY = "trio_help_tab";

// =============================================================================
// Inline Icon Components
// =============================================================================

function SendIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: "middle" }}>
      <circle cx="12" cy="12" r="11" fill="#58a6ff" />
      <path d="M12 16V8M12 8l-3 3M12 8l3 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StopIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: "middle" }}>
      <circle cx="12" cy="12" r="11" fill="#e74c3c" />
      <rect x="9" y="9" width="6" height="6" rx="0.5" fill="white" />
    </svg>
  );
}

function AttachIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ verticalAlign: "middle" }}>
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function CustomQueryIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: "middle" }}>
      <path d="M4 6h16M4 12h10M4 18h14" />
      <circle cx="19" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M18 11h2M19 10v2" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function TempChatIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="4 3" style={{ verticalAlign: "middle" }}>
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

function SettingsIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: "middle" }}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06c.5.5 1.2.67 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.2.63.65 1.13 1.27 1.31H21a2 2 0 0 1 0 4h-.09c-.62.18-1.07.68-1.27 1.31z" />
    </svg>
  );
}

function FriendsIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: "middle" }}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function ShareWebIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor" style={{ verticalAlign: "middle" }}>
      <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
    </svg>
  );
}

function NewChatIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "middle" }}>
      <rect x="4" y="4" width="14" height="16" rx="2" />
      <path d="M21 7l-3-3" />
      <path d="M15 4l3 3-8 8H7v-3l8-8z" />
    </svg>
  );
}

function SearchIcon({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#58a6ff" strokeWidth="2" style={{ verticalAlign: "middle" }}>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function BalanceScaleIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "middle" }}>
      <path d="M12 2v20" />
      <path d="M2 7h20" />
      <path d="M5 7l-3 9h8L7 7" />
      <path d="M17 7l-3 9h8L19 7" />
      <path d="M2 16c0 1.5 2.2 3 4 3s4-1.5 4-3" />
      <path d="M14 16c0 1.5 2.2 3 4 3s4-1.5 4-3" />
      <circle cx="12" cy="2" r="1.5" fill="var(--accent)" stroke="none" />
      <path d="M9 22h6" />
    </svg>
  );
}

function CopyIcon({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "middle" }}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}

function CloseXIcon({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "middle" }}>
      <path d="M18 6L6 18" />
      <path d="M6 6l12 12" />
    </svg>
  );
}

function LockShieldIcon({ size = 16, color }: { size?: number; color?: string }) {
  // Lock-shield motif — matches the SF Symbol "lock.shield" used on iOS CalloutBox (.info).
  const c = color ?? "var(--accent)";
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
      <path d="M12 2l8 3v6c0 5-3.5 9-8 11-4.5-2-8-6-8-11V5l8-3z" />
      <rect x="9" y="11" width="6" height="5" rx="1" />
      <path d="M10 11V9.5a2 2 0 0 1 4 0V11" />
    </svg>
  );
}

function WarningTriangleIcon({ size = 16, color }: { size?: number; color?: string }) {
  // Triangle-exclamation motif — matches the SF Symbol "exclamationmark.triangle" on iOS CalloutBox (.warning).
  const c = color ?? "#f59e0b";
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
      <path d="M12 3l9.5 16.5h-19L12 3z" />
      <path d="M12 9v5" />
      <circle cx="12" cy="17.2" r="0.6" fill={c} stroke="none" />
    </svg>
  );
}

// =============================================================================
// Styled Inline Elements
// =============================================================================

function ProviderBadge({ letter, color }: { letter: string; color: string }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 20,
        height: 20,
        borderRadius: "50%",
        fontSize: "11px",
        fontWeight: 700,
        color: "white",
        background: color,
        verticalAlign: "middle",
        margin: "0 2px",
      }}
    >
      {letter}
    </span>
  );
}

const ANTHROPIC_COLOR = "#cc6144";
const GEMINI_COLOR = "#6b5bff";
const OPENAI_COLOR = "#14a37f";

// Three equal-width provider columns — colored circle above a centered label.
// Mirrors the iOS Quick-tab provider row.
function ProviderColumns() {
  const providers: { letter: string; color: string; label: string }[] = [
    { letter: "A", color: ANTHROPIC_COLOR, label: "Anthropic (Claude)" },
    { letter: "G", color: GEMINI_COLOR, label: "Gemini" },
    { letter: "O", color: OPENAI_COLOR, label: "OpenAI (ChatGPT)" },
  ];
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 10,
        margin: "4px 0",
      }}
    >
      {providers.map((p) => (
        <div
          key={p.letter}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
            textAlign: "center",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 22,
              height: 22,
              borderRadius: "50%",
              fontSize: "12px",
              fontWeight: 700,
              color: "white",
              background: p.color,
            }}
          >
            {p.letter}
          </span>
          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
            {p.label}
          </span>
        </div>
      ))}
    </div>
  );
}

function ModePill({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        background: "#1c2129",
        border: "1px solid var(--accent)",
        borderRadius: "12px",
        padding: "2px 10px 2px 6px",
        fontSize: "13px",
        fontWeight: 500,
        color: "var(--accent)",
        verticalAlign: "middle",
        margin: "0 2px",
        whiteSpace: "nowrap",
      }}
    >
      {icon}
      {label}
    </span>
  );
}

function InstantIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="#58a6ff" style={{ verticalAlign: "middle" }}>
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  );
}

function LowIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#58a6ff" strokeWidth="2" style={{ verticalAlign: "middle" }}>
      <path d="M5 19L19 5" />
      <path d="M8 19H5v-3M16 5h3v3" />
    </svg>
  );
}

function MediumIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#58a6ff" strokeWidth="2" strokeLinecap="round" style={{ verticalAlign: "middle" }}>
      <path d="M5 17l4-4 3 3 7-8" />
      <path d="M15 8h4v4" />
    </svg>
  );
}

function HighIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#58a6ff" strokeWidth="2" strokeLinecap="round" style={{ verticalAlign: "middle" }}>
      <path d="M12 20V4M7 9l5-5 5 5" />
    </svg>
  );
}

function MaxIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="#58a6ff" style={{ verticalAlign: "middle" }}>
      <polygon points="12,2 15,9 22,9 16.5,14 18.5,21 12,17 5.5,21 7.5,14 2,9 9,9" />
    </svg>
  );
}

function DeepResearchIcon() {
  return <SearchIcon size={13} />;
}

function AutoIcon() {
  // Wand-and-stars motif — matches the SF Symbol used for Auto on iOS.
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="#58a6ff" style={{ verticalAlign: "middle" }}>
      <path d="M14 4l2 2M3 21l11-11M18 8l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2zM6 3l.6 1.4L8 5l-1.4.6L6 7l-.6-1.4L4 5l1.4-.6L6 3z" />
    </svg>
  );
}

function ModelSelectorIcon() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        background: "#1c2129",
        border: "1px solid var(--border)",
        borderRadius: "6px",
        padding: "2px 8px 2px 5px",
        fontSize: "13px",
        fontWeight: 500,
        verticalAlign: "middle",
        margin: "0 2px",
        whiteSpace: "nowrap",
      }}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="#58a6ff" style={{ verticalAlign: "middle" }}>
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      </svg>
      A/G/O
    </span>
  );
}

// =============================================================================
// Reusable Layout Components
// =============================================================================

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "#1c2129",
        border: "1px solid #30363d",
        borderRadius: 12,
        padding: "16px 20px",
        marginBottom: 16,
      }}
    >
      {title && (
        <h3
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: "#e6edf3",
            margin: "0 0 10px 0",
          }}
        >
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}

// CalloutBox — mirrors the iOS HelpView CalloutBox (.info / .warning):
//   rounded 12px box · accent at 6% opacity background · 3px solid accent left
//   border · 14px padding · accent-colored icon top-aligned at the left with a
//   12px gap · text block paragraphs at ~6px vertical spacing.
function CalloutBox({
  color,
  children,
}: {
  color: "blue" | "amber";
  children: React.ReactNode;
}) {
  const accentColor = color === "blue" ? "var(--accent)" : "#f59e0b";
  const bgColor =
    color === "blue"
      ? "color-mix(in srgb, var(--accent) 6%, transparent)"
      : "rgba(245, 158, 11, 0.06)";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        borderLeft: `3px solid ${accentColor}`,
        background: bgColor,
        borderRadius: 12,
        padding: 14,
        marginBottom: 14,
      }}
    >
      <span style={{ flexShrink: 0, marginTop: 2, lineHeight: 0 }}>
        {color === "blue" ? (
          <LockShieldIcon size={15} color={accentColor} />
        ) : (
          <WarningTriangleIcon size={15} color={accentColor} />
        )}
      </span>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
        {children}
      </div>
    </div>
  );
}

function ModesTable({ rows, columnCount }: { rows: React.ReactNode[][]; columnCount: number }) {
  const gridTemplateColumns = columnCount === 1
    ? "max-content"
    : columnCount === 2 ? "max-content 1fr" : `max-content repeat(${columnCount - 1}, minmax(0, 1fr))`;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns,
        gap: "12px 14px",
        alignItems: "start",
        fontSize: 14,
        lineHeight: 1.6,
        color: "#e6edf3",
      }}
    >
      {rows.flatMap((row, rowIndex) => row.map((cell, cellIndex) => (
        cellIndex === 0 ? (
          <div
            key={`mt-${rowIndex}-${cellIndex}`}
            style={{ display: "flex", alignItems: "center", minHeight: 22, whiteSpace: "nowrap" }}
          >
            {cell}
          </div>
        ) : <div key={`mt-${rowIndex}-${cellIndex}`}>{cell}</div>
      )))}
    </div>
  );
}

const helpBody: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.6,
  color: "#e6edf3",
  margin: 0,
};

function tierIcon(id: string): React.ReactNode {
  switch (id) {
    case "instant": return <InstantIcon />;
    case "low": return <LowIcon />;
    case "medium": return <MediumIcon />;
    case "high": return <HighIcon />;
    case "max": return <MaxIcon />;
    case "deep_research": return <DeepResearchIcon />;
    default: return null;
  }
}

function renderHelpTable(block: TableBlock) {
  const gridTemplateColumns = block.columns.length === 1
    ? "max-content"
    : block.columns.length === 2 ? "max-content 1fr" : `max-content repeat(${block.columns.length - 1}, minmax(0, 1fr))`;
  return (
    <div key={block.id} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "grid", gridTemplateColumns, gap: 14, fontWeight: 600 }}>
        {block.columns.map((column) => <span key={column}>{column}</span>)}
      </div>
      <ModesTable
        columnCount={block.columns.length}
        rows={block.rows.map((row) => row.cells.map((cell, index) => (
          index === 0
            ? <ModePill icon={tierIcon(row.id)} label={cell.replace(/\*\*(.*?)\*\*/g, "$1")} />
            : <MarkdownRenderer content={cell} />
        )))}
      />
    </div>
  );
}

function HelpDocumentRenderer({ document }: { document: HelpDocument }) {
  return (
    <div style={{ fontSize: 14, lineHeight: 1.6, display: "flex", flexDirection: "column", gap: 14 }}>
      {document.blocks.map((block: HelpBlock) => {
        if (block.type === "table") return renderHelpTable(block);
        if (block.type === "list") {
          const List = block.ordered ? "ol" : "ul";
          return (
            <List key={block.id} style={{ ...helpBody, paddingLeft: 24 }}>
              {block.items.map((item, index) => (
                <li key={index}>
                  <div style={helpBody}><MarkdownRenderer content={item} /></div>
                </li>
              ))}
            </List>
          );
        }
        if (block.type === "heading") return <h3 key={block.id} style={completeHeader}>{block.text}</h3>;
        if (block.type === "callout") {
          return <CalloutBox key={block.id} color={block.style === "warning" ? "amber" : "blue"}><div style={helpBody}><MarkdownRenderer content={block.text} /></div></CalloutBox>;
        }
        return <div key={block.id} style={helpBody}><MarkdownRenderer content={block.text} /></div>;
      })}
    </div>
  );
}

function useHelpDocument(id: string): HelpDocument | undefined {
  const [, setContentEpoch] = useState(0);
  useEffect(() => subscribeToContent(() => setContentEpoch((epoch) => epoch + 1)), []);
  return getContent()[id] as HelpDocument | undefined;
}

function ToolbarItem({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        minWidth: 0,
      }}
    >
      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 20 }}>{icon}</span>
      <span
        style={{
          fontSize: 9,
          color: "var(--text-secondary)",
          whiteSpace: "nowrap",
          letterSpacing: "0.2px",
        }}
      >
        {label}
      </span>
    </div>
  );
}

// =============================================================================
// Tab Content: Quick
// =============================================================================

function QuickTab() {
  const document = useHelpDocument("help.quick");
  if (document) return <HelpDocumentRenderer document={document} />;

  const quickBody: React.CSSProperties = { color: "#e6edf3", margin: 0 };
  const blockGap: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  };
  return (
    <div style={{ fontSize: 14, lineHeight: 1.6, display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={blockGap}>
        <p style={quickBody}>
          Welcome to Trio. The idea is simple: your question goes to three AI providers at once — Anthropic, OpenAI and Google — and you see all three answers side by side.
        </p>
        <p style={quickBody}>
          Submitting with the balance-scale button also has an AI panel judge all three answers and pick a winner, then write a combined best-of answer. That is the default way to send. If you already have answers on screen and want them judged afterwards, there is a Judge button in the top bar.
        </p>
        <CalloutBox color="blue">
          <p style={{ ...quickBody, fontWeight: 500, marginBottom: 0 }}>
            Your saved chats and the files you attach are encrypted with a key unique to your account.
          </p>
        </CalloutBox>
      </div>

      <div style={blockGap}>
        <p style={quickBody}>
          <strong>The toolbar above the input is your control centre.</strong> The mode button decides how hard the models think. The default is <strong>Auto</strong>, which reads your question and picks a level for you. You can override it with any of these:
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "max-content 1fr",
            gap: 14,
            fontWeight: 600,
          }}
        >
          <span>Tier</span>
          <span>What it is for</span>
        </div>
        <ModesTable
          columnCount={2}
          rows={[
            [<ModePill icon={<InstantIcon />} label="Instant" />, <>An immediate answer from what the model already knows. No web search.</>],
            [<ModePill icon={<LowIcon />} label="Low" />, <>A quick answer, still able to look things up.</>],
            [<ModePill icon={<MediumIcon />} label="Medium" />, <>The everyday setting. Reasoning plus web search.</>],
            [<ModePill icon={<HighIcon />} label="High" />, <>Medium with more room to think and explain.</>],
            [<ModePill icon={<MaxIcon />} label="Max" />, <>The deepest single answer, for hard questions.</>],
            [<ModePill icon={<DeepResearchIcon />} label="Deep Research" />, <>A long multi-step investigation that returns a written report. Minutes, not seconds.</>],
          ]}
        />
        <p style={quickBody}>
          Auto never starts Deep Research on its own, because a report takes minutes and costs more. Choose Deep Research yourself when you want one.
        </p>
        <p style={quickBody}>
          By default your question goes to all three providers. To ask just one, tap the letter button above the send button: it sends that one question to your <strong>favourite provider</strong>. Choose your favourite in Settings → Single Provider Favorite.
        </p>
        <p style={quickBody}>
          On iPhone, the microphone buttons beside the input start <strong>Voice Mode</strong> — speak your question and Trio reads the answer back to you.
        </p>
      </div>

      <div style={blockGap}>
        <p style={quickBody}>
          Your chats save automatically. Use <strong>Recent</strong> and <strong>Folders</strong> at the top to browse, organise and search. A folder remembers the key facts and decisions from its chats, so follow-up questions pick up the background without you repeating yourself.
        </p>
        <p style={quickBody}>
          Tap play on any answer, or on a finished Deep Research report, to hear it read aloud. Code blocks have a <strong>Copy</strong> button, and every answer shows its token counts underneath.
        </p>
        <p style={quickBody}>
          Share a chat with the share button. That is everything you need — enjoy.
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// Tab Content: Details
// =============================================================================

const detailsBody: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.6,
  color: "#e6edf3",
  marginBottom: 14,
};

const detailsList: React.CSSProperties = {
  margin: "0 0 14px 18px",
  fontSize: 14,
  lineHeight: 1.6,
  color: "#e6edf3",
};

function DetailsTab() {
  const document = useHelpDocument("help.details");
  if (document) return <HelpDocumentRenderer document={document} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      <SectionCard title="The six tiers, and what actually changes">
        <p style={detailsBody}>
          Every tier is a different model and a different thinking budget on each of the three providers. Trio picks the combination for you; you pick the level.
        </p>
        <ul style={detailsList}>
          <li><strong>Instant</strong> — answers from the model&apos;s own knowledge with no web search at all. Fastest and cheapest. Because it cannot look anything up, it is told to say so rather than guess at anything current.</li>
          <li><strong>Low</strong> — a short answer that can still search the web. Good for a fact you need now.</li>
          <li><strong>Medium</strong> — the default for most real questions. Reasoning, web search, and enough room to explain the answer.</li>
          <li><strong>High</strong> — the same shape as Medium with more space to reason and lay out the tradeoffs.</li>
          <li><strong>Max</strong> — the most capable single-shot answer. Slower and more expensive; worth it on genuinely hard questions.</li>
          <li><strong>Deep Research</strong> — not a bigger answer, a different thing. Trio plans an investigation, runs many searches, reads sources, and writes a report with citations. It runs for minutes and costs meaningfully more than any other tier, so choose it deliberately.</li>
        </ul>
        <p style={{ ...detailsBody, marginBottom: 0 }}>
          <strong>Auto</strong> reads your question and picks a tier for you, up to Max. It never starts Deep Research on its own. You can always override it.
        </p>
      </SectionCard>

      <SectionCard title="Deep Research">
        <p style={{ ...detailsBody, marginBottom: 0 }}>
          While a report runs, its card shows each stage and a running count of searches and cost, and you can stop it at any time. The finished report opens as a full document with its sources listed, and you can play it aloud. Follow-up questions in a Deep Research chat go to Anthropic only.
        </p>
      </SectionCard>

      <SectionCard title="Judging and the best-of answer">
        <p style={{ ...detailsBody, marginBottom: 0 }}>
          Three scorers rate all three answers on the same rubric, one from each provider, and a separate model writes the combined answer. The winner badge is the least interesting part; the combined answer is the product. Scores are corrected for a measured bias before a winner is picked.
        </p>
      </SectionCard>

      <SectionCard title="Custom instructions">
        <p style={detailsBody}>
          Settings → <strong>Custom Instructions</strong> is one box where you tell Trio how you want it to answer — for example &quot;I work in finance, skip the basics&quot; or &quot;always give me the number.&quot;
        </p>
        <p style={detailsBody}>
          <strong>What you write is added to Trio&apos;s own instructions, never in place of them.</strong> When you save, Trio checks what you wrote and will tell you what it did, every time:
        </p>
        <ul style={detailsList}>
          <li>If something you wrote conflicts with how Trio has to behave, <strong>Trio adjusts it and tells you what changed and why.</strong></li>
          <li>If what you wrote is a <strong>fact about you</strong> rather than an instruction — &quot;I live in New York&quot; — Trio moves it into <strong>Memory</strong>, which is where facts belong, and takes it out of the instructions.</li>
          <li>If it only makes sense for some kinds of question, Trio rewrites it as a condition, so it applies when it should and stays out of the way when it should not.</li>
        </ul>
        <p style={{ ...detailsBody, marginBottom: 0 }}>
          The box then shows what Trio actually stored, and a note at the top of the tab tells you what happened. You can clear the note once you have read it.
        </p>
      </SectionCard>

      <SectionCard title="Memory">
        <p style={{ ...detailsBody, marginBottom: 0 }}>
          Trio remembers standing facts about you across chats. You can read, edit and delete every one of them in Settings → Memory. Anything moved there by Custom Instructions shows up in the same list.
        </p>
      </SectionCard>

      <SectionCard title="Folders and context">
        <p style={{ ...detailsBody, marginBottom: 0 }}>
          A folder keeps the key facts and decisions from the chats inside it. Start a new chat in that folder and it picks them up, so you do not have to re-explain the background.
        </p>
      </SectionCard>

      <SectionCard title="Attachments">
        <p style={detailsBody}>
          Use the paperclip to attach PDFs, Word, Excel and PowerPoint files, CSV, text and Markdown files, and images. Large PDFs are compressed automatically.
        </p>
        <h3 style={completeHeader}>Custom and scheduled queries</h3>
        <p style={{ ...detailsBody, marginBottom: 0 }}>
          Save a question you ask often in Settings → Custom Queries and run it from the composer. Give it a schedule — daily, weekly, monthly or quarterly — and Trio runs it for you; the results land in your history.
        </p>
      </SectionCard>

      <SectionCard title="Temporary chats">
        <p style={{ ...detailsBody, marginBottom: 0 }}>
          A temporary chat is not saved and is kept out of the logs. Use it for anything you would rather leave no trace of.
        </p>
      </SectionCard>

      <SectionCard title="Voice Mode (iPhone)">
        <p style={{ ...detailsBody, marginBottom: 0 }}>
          Speak your question; Trio answers aloud. Two microphone buttons: one asks a single provider, one asks all three and reads the combined answer. Voice Mode is iPhone only.
        </p>
      </SectionCard>
    </div>
  );
}

// =============================================================================
// Tab Content: Complete
// =============================================================================

const completeBody: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.6,
  color: "#e6edf3",
  marginBottom: 14,
};

const completeHeader: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: "#e6edf3",
  margin: "0 0 8px 0",
};

const completeSubHeader: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "var(--text-secondary)",
  textTransform: "uppercase" as const,
  letterSpacing: "0.5px",
  marginTop: 14,
  marginBottom: 6,
};

const completeList: React.CSSProperties = {
  margin: "6px 0 12px 18px",
  fontSize: 13,
  lineHeight: 1.6,
};

function CompleteTab() {
  const document = useHelpDocument("help.complete");
  if (document) return <HelpDocumentRenderer document={document} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      <SectionCard title="">
        <h3 style={completeHeader}>What Trio is</h3>
        <p style={{ ...completeBody, marginBottom: 0 }}>
          Trio sends one question to Anthropic, OpenAI and Google in parallel, then a panel of three scorers and a separate synthesiser turn the three answers into one. You can read the individual answers, the scores, or just the combined answer.
        </p>
      </SectionCard>

      <SectionCard title="">
        <h3 style={completeHeader}>Tiers in full</h3>
        <p style={completeBody}>
          The six tiers are Instant, Low, Medium, High, Max and Deep Research. Each tier names a specific model and thinking budget for each provider.
        </p>
        <p style={completeBody}>
          <strong>Auto</strong> reads your question and picks a tier for you, per provider, up to Max. It never starts Deep Research on its own. You can override it at any time.
        </p>
        <p style={completeBody}>
          <strong>Deep Research</strong> is a distinct pipeline, not a bigger answer: it plans, searches repeatedly, reads and cites sources, and produces a report. It takes minutes and is by a wide margin the most expensive thing in the app.
        </p>
      </SectionCard>

      <SectionCard title="">
        <h3 style={completeHeader}>Judging</h3>
        <p style={{ ...completeBody, marginBottom: 0 }}>
          Each answer is scored by three scorers on a shared rubric. Scores carry a correction for a measured scorer bias, so a provider is not rewarded for rating its own family highly. A tie is resolved against the biased scorer rather than in its favour. The synthesiser then writes the combined answer from all three, which is the output most people should read.
        </p>
      </SectionCard>

      <SectionCard title="">
        <h3 style={completeHeader}>Instructions, memory and how they combine</h3>
        <p style={completeBody}>
          Four things shape an answer, in this order:
        </p>
        <ul style={completeList}>
          <li><strong>Trio&apos;s own rules</strong> — always present, and nothing you write replaces them.</li>
          <li><strong>The tier&apos;s instructions</strong> — different for every tier; Instant&apos;s, for instance, tells the model it has no web search and must not claim to have looked anything up.</li>
          <li><strong>Your custom instructions</strong> — added after the above, never instead of them.</li>
          <li><strong>Your memories</strong> — standing facts about you.</li>
        </ul>
        <p style={completeBody}>
          Custom instructions are checked before they are saved. Trio will rewrite anything that conflicts with 1 or 2, move standing facts into Memory, and rephrase a request that only suits some questions into a condition. <strong>Every save produces a note telling you what it did, including when it accepted your text unchanged</strong>, and the box shows what was stored rather than what you typed.
        </p>
        <p style={{ ...completeBody, marginBottom: 0 }}>
          There is no per-tier prompt editing and no parameter tuning.
        </p>
      </SectionCard>

      <SectionCard title="">
        <h3 style={completeHeader}>Providers</h3>
        <p style={{ ...completeBody, marginBottom: 0 }}>
          Which three providers are active, which models each tier uses, and their display order all come from the server. They can change without an app update.
        </p>
      </SectionCard>

      <SectionCard title="">
        <h3 style={completeHeader}>Privacy</h3>
        <p style={{ ...completeBody, marginBottom: 0 }}>
          Your saved chats and the files you attach are encrypted at rest with a key unique to your account. Temporary chats are excluded from the logs. <strong>AI Data Sharing</strong> in Settings → Privacy explains exactly what is sent to the providers, and you can review it at any time. Sharing a chat is always an explicit act.
        </p>
      </SectionCard>

      <SectionCard title="">
        <h3 style={completeHeader}>History, folders, search and sharing</h3>
        <p style={{ ...completeBody, marginBottom: 0 }}>
          Chats save automatically and can be organised into folders, each of which keeps the key facts and decisions from its chats for new chats in that folder to use. Search covers your whole history. Sharing produces a public link containing a snapshot of that chat, and you can delete the link later.
        </p>
      </SectionCard>

      <SectionCard title="">
        <h3 style={completeHeader}>Usage</h3>
        <p style={{ ...completeBody, marginBottom: 0 }}>
          Settings → Usage shows what you have spent, by tier and by provider. Deep Research dominates any period in which you ran one.
        </p>
      </SectionCard>

      <SectionCard title="">
        <h3 style={completeHeader}>Admin</h3>
        <p style={{ ...completeBody, marginBottom: 0 }}>
          Only visible on an admin account. Not part of the normal app.
        </p>
      </SectionCard>
    </div>
  );
}

// =============================================================================
// HelpContent — reusable Quick/Details/Complete tabs (no modal chrome)
// =============================================================================

export function HelpContent() {
  const [activeTab, setActiveTab] = useState<number>(() => {
    if (typeof window !== "undefined") {
      return parseInt(sessionStorage.getItem(TAB_STORAGE_KEY) || "0", 10);
    }
    return 0;
  });

  useEffect(() => {
    sessionStorage.setItem(TAB_STORAGE_KEY, String(activeTab));
  }, [activeTab]);

  const tabs = ["Quick", "Details", "Complete"];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ display: "flex", borderBottom: "1px solid var(--border)" }}>
        {tabs.map((tab, i) => (
          <button
            key={tab}
            onClick={() => setActiveTab(i)}
            style={{
              flex: 1,
              padding: "8px 0",
              border: "none",
              background: "none",
              color: activeTab === i ? "var(--accent)" : "var(--text-secondary)",
              fontWeight: activeTab === i ? 600 : 400,
              fontSize: "14px",
              cursor: "pointer",
              borderBottom: activeTab === i ? "2px solid var(--accent)" : "2px solid transparent",
              marginBottom: "-1px",
            }}
          >
            {tab}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 4px" }}>
        {activeTab === 0 && <QuickTab />}
        {activeTab === 1 && <DetailsTab />}
        {activeTab === 2 && <CompleteTab />}
      </div>
    </div>
  );
}

// =============================================================================
// HelpModal
// =============================================================================

export default function HelpModal({ isOpen, onClose, isMobile }: HelpModalProps) {
  const [activeTab, setActiveTab] = useState<number>(() => {
    if (typeof window !== "undefined") {
      return parseInt(sessionStorage.getItem(TAB_STORAGE_KEY) || "0", 10);
    }
    return 0;
  });

  useEffect(() => {
    sessionStorage.setItem(TAB_STORAGE_KEY, String(activeTab));
  }, [activeTab]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const tabs = ["Quick", "Details", "Complete"];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: isMobile ? "95vw" : "650px",
          maxWidth: "95vw",
          height: isMobile ? "90vh" : "80vh",
          padding: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px 0" }}>
          <h2 style={{ fontSize: "18px", fontWeight: 600, margin: 0 }}>Help</h2>
          <button className="btn-ghost" onClick={onClose} style={{ fontSize: "14px" }}>Done</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid var(--border)", padding: "0 20px", marginTop: "12px" }}>
          {tabs.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              style={{
                flex: 1,
                padding: "8px 0",
                border: "none",
                background: "none",
                color: activeTab === i ? "var(--accent)" : "var(--text-secondary)",
                fontWeight: activeTab === i ? 600 : 400,
                fontSize: "14px",
                cursor: "pointer",
                borderBottom: activeTab === i ? "2px solid var(--accent)" : "2px solid transparent",
                marginBottom: "-1px",
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
          {activeTab === 0 && <QuickTab />}
          {activeTab === 1 && <DetailsTab />}
          {activeTab === 2 && <CompleteTab />}
        </div>
      </div>
    </div>
  );
}
